import { ApolloError } from '@apollo/client';
import { getDispatchDate } from '@utils/dispatch';
import { framedByDefault } from '@utils/frames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { COUNTRIES } from '../../../constants/locations';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useCurrency } from '../../../hooks/useCurrency';
import { useAuth } from '../../../providers';
import { useDraws } from '../../../providers/DrawProvider';
import { AccountGateway, CountryCode } from '../../../types/Account';
import { AuthGateway } from '../../../types/Auth';
import {
  GeneratedAddress,
  GeneratedCreateDrawEntryIntentMutation,
  GeneratedDrawType,
  GeneratedOrderItem,
  GeneratedPaymentMethod,
  useCreateDrawEntryIntentMutation,
  useUpdateDrawEntryIntentMutation,
} from '../../../types/generated';
import { CurrencyCode } from '../../../types/Locations';
import { PaymentGateway } from '../../../types/Payment';
import { Frame } from '../../../types/Products';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { captureError } from '../../../utils/capture';
import { shopifyGraphqlIdToAdminId } from '../../../utils/shopifyIds';
import { sortIso8601 } from '../../../utils/sort';
import {
  DrawWithFrames,
  PaymentType,
  PreAuthDrawController,
  StripeAddressDetails,
} from '../types';

interface PreAuthDrawOptions {
  authGateway: AuthGateway;
  accountGateway: AccountGateway;
  paymentGateway: PaymentGateway;
}

interface BidTooLowExtensions {
  code: 'BID_TOO_LOW';
  details?: {
    floor_price: string;
    floor_price_currency_code: string;
  };
}

export function usePreAuthDraw(
  options: PreAuthDrawOptions,
): PreAuthDrawController {
  const { t } = useTranslation('product');
  const { user } = useAuth();
  const { accountGateway, paymentGateway } = options;
  const [countryCode, setCountry] = useCountryCode();
  const [frameEmitter] = useAnalyticsEmitter();
  const [customerSelectedFrame, setCustomerSelectedFrame] = useState<
    Frame | null | undefined
  >();

  const { updateCurrentWinningBidByDrawId } = useDraws();

  const [activeDraw, setActiveDraw] = useState<DrawWithFrames | null>(null);
  const { draw, product } = activeDraw || {};

  const [bidValidationError, setBidValidationError] = useState<string | null>(
    null,
  );

  const [setupComplete, setSetupComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [isPreOrderSelected, setIsPreOrderSelected] = useState(false);

  const [setupSecret, setSetupSecret] = useState<string>();
  const [pricing, setPricing] =
    useState<
      NonNullable<
        Required<GeneratedCreateDrawEntryIntentMutation>['createDrawEntryIntent']
      >['pricing']
    >();
  const [readyToConfirm, setReadyToConfirm] = useState(false);
  const [cryptoComPaymentId, setCryptoComPaymentId] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string | true>();

  const [selectedAddress, setSelectedAddress] = useState<string>();
  const [addresses, setAddresses] = useState<GeneratedAddress[]>([]);
  const selectedAddressCountryCode = addresses.find(
    (a) => a.id === selectedAddress,
  )?.countryCode;

  const [bid, setBid] = useState<number>();

  const [currencyCode] = useCurrency();
  const currency =
    COUNTRIES[selectedAddressCountryCode as CountryCode]?.currency ??
    currencyCode;

  const shippingCurrency =
    draw?.drawType === GeneratedDrawType.Auction && draw.auctionReservePrice
      ? draw.auctionReservePrice.currencyCode
      : currency;

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [paymentMethods, setPaymentMethods] = useState<
    GeneratedPaymentMethod[]
  >([]);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentType | null>(null);

  const [optionalItems, setOptionalItems] = useState<GeneratedOrderItem[]>([]);

  const [drawEntryIntent, setDrawEntryIntent] =
    useState<
      NonNullable<
        GeneratedCreateDrawEntryIntentMutation['createDrawEntryIntent']
      >['intent']
    >();

  const [billingAddress, setBillingAddress] =
    useState<StripeAddressDetails | null>(null);
  const [usingNewPaymentMethod, setUsingNewPaymentMethod] = useState(false);
  const [newPaymentMethodType, setNewPaymentMethodType] = useState<string>();
  const [paymentErrorCode, setPaymentErrorCode] = useState<string | null>(null);
  const [
    newPaymentMethodUsingShippingAddress,
    setNewPaymentMethodUsingShippingAddress,
  ] = useState(true);

  const resetController = useCallback(() => {
    setActiveDraw(null);
    setSetupComplete(false);
    setLoading(false);
    setUpdating(false);
    setSetupSecret(undefined);
    setPricing(undefined);
    setReadyToConfirm(false);
    setErrorMessage(undefined);
    setSelectedAddress(undefined);
    setAddresses([]);
    setBid(undefined);
    setSelectedPaymentMethodId(null);
    setPaymentMethods([]);
    setSelectedPaymentType(null);
    setDrawEntryIntent(undefined);
    setBillingAddress(null);
    setUsingNewPaymentMethod(false);
    setNewPaymentMethodType(undefined);
    setPaymentErrorCode(null);
    setNewPaymentMethodUsingShippingAddress(true);
    setOptionalItems([]);
    setCustomerSelectedFrame(null);
    setCryptoComPaymentId(undefined);
  }, []);

  // TODO: check product.launchAt works correctly
  const dispatchDate = useMemo(
    () =>
      getDispatchDate({
        framed:
          framedByDefault(product?.framingOffered) ||
          Boolean(customerSelectedFrame),
        framedDispatchDate: product?.framedDispatchDate || null,
        unframedDispatchDate: product?.unframedDispatchDate || null,
        launchDate: product?.launchAt ?? '',
      }),
    [
      customerSelectedFrame,
      product?.framedDispatchDate,
      product?.framingOffered,
      product?.launchAt,
      product?.unframedDispatchDate,
    ],
  );

  async function fetchAddresses(): Promise<string | null> {
    const addressesResponse = await accountGateway.getAddresses();

    if (!Array.isArray(addressesResponse)) return null;

    const addressesWithPhoneNumber = addressesResponse.filter((address) =>
      Boolean(address.phone),
    );

    setAddresses(addressesWithPhoneNumber);

    const defaultAddress = addressesWithPhoneNumber.find((a) => a.isDefault);

    if (defaultAddress) {
      setSelectedAddress(defaultAddress.id);

      return defaultAddress.id;
    }

    const latestAddress = addressesWithPhoneNumber.sort(
      sortIso8601((a) => a.updatedAt || a.createdAt || '2020-01-01T00:00:00Z'),
    )[0];

    if (latestAddress) setSelectedAddress(latestAddress.id);

    return null;
  }

  async function fetchPaymentMethods(): Promise<string | null> {
    const paymentMethodsResponse = await paymentGateway.listPaymentMethods();

    if (!Array.isArray(paymentMethodsResponse)) return null;

    setPaymentMethods(paymentMethodsResponse);

    const defaultPm = paymentMethodsResponse.find((p) => p.default);

    if (defaultPm) {
      setSelectedPaymentMethodId(defaultPm.providerPaymentMethodId);
      setSelectedPaymentType(PaymentType.CARD);

      return defaultPm.providerPaymentMethodId;
    }

    const firstPaymentMethod = paymentMethodsResponse[0];

    if (firstPaymentMethod) {
      setSelectedPaymentMethodId(firstPaymentMethod.providerPaymentMethodId);
      setSelectedPaymentType(PaymentType.CARD);
    }

    return null;
  }

  const [mutateCreateDei] = useCreateDrawEntryIntentMutation();

  async function setupController(drawWithFramesForSetup: DrawWithFrames) {
    if (!drawWithFramesForSetup) return;

    setActiveDraw(drawWithFramesForSetup);
    setLoading(true);

    const { draw: currentDraw } = drawWithFramesForSetup;

    try {
      const defaultOptionalItems: GeneratedOrderItem[] = [];
      const [defaultShippingAddressId, defaultPaymentMethodId] =
        await Promise.all([fetchAddresses(), fetchPaymentMethods()]);

      // Check frame on purchasing controller
      if (customerSelectedFrame) {
        defaultOptionalItems.push({
          productID: shopifyGraphqlIdToAdminId(
            customerSelectedFrame.variant.consolidatedVariants[0]
              .shopifyFrameId,
          ),
          variantID: shopifyGraphqlIdToAdminId(
            customerSelectedFrame.variant.consolidatedVariants[0]
              .shopifyFrameVariantId,
          ),
          quantity: 1,
        });
      }

      setOptionalItems(defaultOptionalItems);

      const res = await mutateCreateDei({
        variables: {
          drawId: currentDraw.id,
          currencyCode: shippingCurrency as CurrencyCode,
          shippingAddressId: defaultShippingAddressId ?? null,
          paymentMethodId: defaultPaymentMethodId ?? null,
          optionalItems: defaultOptionalItems,
          preOrder: isPreOrderSelected,
        },
      });

      const createDeiResponse = res.data?.createDrawEntryIntent;

      if (!createDeiResponse || res.errors) {
        throw new Error(
          `Unable to create draw entry intent draw_id:${currentDraw.id}`,
        );
      }

      // Update state
      setDrawEntryIntent(createDeiResponse.intent);
      setPricing(createDeiResponse.pricing);
      setReadyToConfirm(false);
      setSetupSecret(createDeiResponse.setupSecret);

      // lazyboi
      setSetupComplete(true);
      setErrorMessage(void 0);
    } catch (error) {
      if (error instanceof Error) {
        captureError(error);
      }

      setErrorMessage(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDiscardedDrawEntryIntent = useCallback(async () => {
    if (!draw) return;
    console.warn('Discarded draw entry intent detected, creating new.');

    try {
      const res = await mutateCreateDei({
        variables: {
          drawId: draw.id,
          currencyCode: shippingCurrency as CurrencyCode,
          shippingAddressId: selectedAddress,
          paymentMethodId: selectedPaymentMethodId,
          optionalItems,
          preOrder: isPreOrderSelected,
          cryptoPaymentRequested:
            selectedPaymentType === null
              ? null
              : selectedPaymentType === PaymentType.CRYPTO,
        },
      });

      const createDeiResponse = res.data?.createDrawEntryIntent;

      if (!createDeiResponse || res.errors) {
        throw new Error(
          `Unable to create draw entry intent draw_id:${draw.id}`,
        );
      }

      // Update state
      setPricing(createDeiResponse.pricing);
      setDrawEntryIntent(createDeiResponse.intent);
      setSetupSecret(createDeiResponse.setupSecret);
      setSetupComplete(true);
      setErrorMessage(void 0);
    } catch (error) {
      setErrorMessage(true);
      console.error(error);
    }
  }, [
    shippingCurrency,
    draw,
    optionalItems,
    selectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
    isPreOrderSelected,
    mutateCreateDei,
  ]);

  const [mutateUpdateDei] = useUpdateDrawEntryIntentMutation();

  const updateDrawEntryIntent = useCallback(async () => {
    if (!user || !draw || !drawEntryIntent?.id) return;

    setUpdating(true);

    try {
      const res = await mutateUpdateDei({
        variables: {
          id: drawEntryIntent.id,
          currencyCode: shippingCurrency as CurrencyCode,
          shippingAddressId: selectedAddress ?? null,
          paymentMethodId: selectedPaymentMethodId ?? null,
          optionalItems,
          preOrder: isPreOrderSelected,
          cryptoPaymentRequested:
            selectedPaymentType === null
              ? null
              : selectedPaymentType === PaymentType.CRYPTO,
          bid: bid
            ? { amount: bid?.toString(), currencyCode: shippingCurrency }
            : undefined,
        },
      });

      const response = res.data?.updateDrawEntryIntent;

      if (!response || res.errors) {
        throw new Error(
          `Unable to update draw entry intent draw_entry_intent:${drawEntryIntent.id}`,
        );
      }

      setBidValidationError(null);
      setPricing(response.pricing);
      setDrawEntryIntent(response.intent);
      setReadyToConfirm(response.readyToConfirm);
      setCryptoComPaymentId(response.cryptoComPaymentId || undefined);
      setErrorMessage(void 0);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'draw entry intent has been discarded'
      ) {
        await handleDiscardedDrawEntryIntent();

        return;
      }

      setErrorMessage(true);
      console.error(error);

      if (!(error instanceof ApolloError)) {
        return;
      }

      const gqlError = error.graphQLErrors[0];
      const extensions = gqlError?.extensions as
        | BidTooLowExtensions
        | undefined;

      if (extensions?.code === 'BID_TOO_LOW') {
        const winningBid = extensions.details?.floor_price;
        const winningBidCurrencyCode =
          extensions.details?.floor_price_currency_code ?? '';

        updateCurrentWinningBidByDrawId(draw.id, {
          amount: winningBid ?? '',
          currencyCode: winningBidCurrencyCode ?? '',
        });

        setBidValidationError(
          t('drawers.bid.prompt.errorTooLow', { amount: winningBid }),
        );
      }
    } finally {
      setUpdating(false);
    }
  }, [
    user,
    draw,
    drawEntryIntent?.id,
    mutateUpdateDei,
    shippingCurrency,
    selectedAddress,
    selectedPaymentMethodId,
    optionalItems,
    isPreOrderSelected,
    selectedPaymentType,
    bid,
    handleDiscardedDrawEntryIntent,
    updateCurrentWinningBidByDrawId,
    t,
  ]);

  const handleUpdateAddress = useCallback(
    (addressId: string) => {
      const selectedAddressDetails = (addresses || []).find((address) => {
        return address.id === addressId;
      });
      if (
        selectedAddressDetails &&
        selectedAddressDetails.countryCode &&
        selectedAddressDetails.countryCode !== countryCode
      ) {
        setCountry(selectedAddressDetails.countryCode as CountryCode);
      }
    },
    [addresses, countryCode, setCountry],
  );

  async function refreshAddresses(newSelectedAddressId: string | undefined) {
    const addressesResponse = await accountGateway.getAddresses();

    if (!Array.isArray(addressesResponse)) return;

    setAddresses(addressesResponse);

    if (newSelectedAddressId !== void 0) {
      setSelectedAddress(newSelectedAddressId);

      return;
    }

    const defaultAddress = addressesResponse.find((a) => a.isDefault);

    if (defaultAddress) setSelectedAddress(defaultAddress.id);
  }

  useEffect(() => {
    if (!setupComplete) return;

    updateDrawEntryIntent();
  }, [
    setupComplete,
    selectedAddress,
    selectedPaymentMethodId,
    selectedPaymentType,
    updateDrawEntryIntent,
  ]);

  useEffect(() => {
    if (customerSelectedFrame) {
      setOptionalItems([
        {
          productID: shopifyGraphqlIdToAdminId(
            customerSelectedFrame.variant.consolidatedVariants[0]
              .shopifyFrameId,
          ),
          variantID: shopifyGraphqlIdToAdminId(
            customerSelectedFrame.variant.consolidatedVariants[0]
              .shopifyFrameVariantId,
          ),
          quantity: 1,
        },
      ]);

      frameEmitter('frame', 'selected', {
        'frame name': customerSelectedFrame.title,
        'frame price':
          customerSelectedFrame.variant.consolidatedVariants[0].price,
      });
    } else {
      setOptionalItems([]);
    }
  }, [frameEmitter, customerSelectedFrame]);

  // TODO: remove this
  // useEffect(() => {
  //   setSetupComplete(false);
  // }, [shopifyId]);

  useEffect(() => {
    if (selectedAddress) handleUpdateAddress(selectedAddress);
  }, [handleUpdateAddress, selectedAddress]);

  return {
    loading: (drawId: string) => {
      return loading && draw?.id === drawId;
    },
    bid,
    setBid,
    updating,
    draw,
    setupController,
    resetController,
    setupSecret,
    updateDrawEntryIntent,
    entryIntentResponse: {
      intent: drawEntryIntent,
      pricing,
      readyToConfirm,
      cryptoComPaymentId,
    },
    setupComplete,

    errorMessage,

    setSelectedAddress,
    selectedAddress,
    addresses,
    refreshAddresses,

    setSelectedPaymentMethodId,
    selectedPaymentMethodId,
    paymentMethods,

    selectedPaymentType,
    setSelectedPaymentType,

    usingNewPaymentMethod,
    newPaymentMethodType,
    paymentErrorCode,
    setPaymentErrorCode,
    setNewPaymentMethodType,
    newPaymentMethodUsingShippingAddress,
    setUsingNewPaymentMethod,
    setNewPaymentMethodUsingShippingAddress,
    billingAddress,
    setBillingAddress,

    optionalItems,
    setOptionalItems,
    setCustomerSelectedFrame,
    customerSelectedFrame,
    dispatchDate,
    activeDraw,
    isPreOrderSelected,
    setIsPreOrderSelected,
    bidValidationError,
  };
}
